import { useEffect, useId, useRef, useState } from "react";
import { Trash2, X } from "lucide-react";
import { deleteProjectNode, deleteProjectNodeImage, getProjectNodes } from "lib/api/projectStructure";
import "css/projectNodeDelete.css";

function ConfirmDelete({ projectId, node, hasChildren, onDeleted, onClose }) {
  const ref = useRef(null), busyRef = useRef(false), titleId = useId();
  const [busy, setBusy] = useState(false), [message, setMessage] = useState("");
  useEffect(() => {
    const dialog = ref.current, previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); if (previous?.isConnected) previous.focus(); };
  }, []);
  const remove = async () => {
    if (busyRef.current || hasChildren) return;
    busyRef.current = true; setBusy(true); setMessage("");
    try {
      // Recheck before deleting the image so a newly added child blocks both operations.
      const latest = await getProjectNodes(projectId);
      if (!latest.success) throw new Error(latest.message);
      const nodes = latest.data.nodes;
      if (nodes.some(item => item.parent_id === node.id)) throw new Error("하위 항목가 추가되었습니다. 하위 항목를 먼저 삭제해 주세요.");
      const current = nodes.find(item => item.id === node.id);
      if (current?.image_path) {
        if (current.image_path !== node.image_path) throw new Error("대표 이미지가 변경되었습니다. 목록을 다시 조회해 주세요.");
        const image = await deleteProjectNodeImage(projectId, node.id, current.image_path);
        if (!image.success) throw new Error(image.message);
      }
      const result = await deleteProjectNode(projectId, node.id);
      if (!result.success) throw new Error(result.message);
      await onDeleted?.(result.data.nodes);
      onClose();
    } catch (error) { setMessage(error.message || "처리 결과를 확인하지 못했습니다. 목록을 다시 조회하세요."); }
    finally { busyRef.current = false; setBusy(false); }
  };
  return <dialog ref={ref} className="projectNodeDeleteDialog" aria-labelledby={titleId} onCancel={event => { event.preventDefault(); if (!busyRef.current) onClose(); }}>
    <div className="projectNodeDeleteDialog__heading"><h3 id={titleId}>항목 삭제</h3><button type="button" aria-label="항목 삭제 창 닫기" disabled={busy} onClick={onClose}><X size={18} /></button></div>
    <p><strong>{node.name}</strong> 항목를 삭제하시겠습니까?</p>
    {hasChildren ? <p role="alert">하위 항목가 있어 삭제할 수 없습니다. 하위 항목부터 삭제해 주세요.</p> : <p>파일은 삭제되지 않습니다. 연결된 파일과 보고서의 해당 건물·층·위치 정보만 ‘미지정’으로 변경됩니다. 항목와 대표 이미지 삭제는 되돌릴 수 없습니다.</p>}
    {message && <p role="alert">{message} 대표 이미지가 먼저 삭제되었을 수 있으므로 목록을 다시 확인해 주세요.</p>}
    <div className="projectNodeDeleteDialog__actions"><button type="button" disabled={busy} onClick={onClose}>취소</button><button type="button" className="is-danger" disabled={busy || hasChildren} onClick={remove}>{busy ? "삭제 중…" : "항목 삭제"}</button></div>
  </dialog>;
}

export default function ProjectNodeDeleteButton({ projectId, node, hasChildren, onDeleted, disabled = false }) {
  const [open, setOpen] = useState(false);
  return <><button type="button" className="projectNodeDelete" aria-label={`${node.name} 항목 삭제`} title="항목 삭제" disabled={disabled} onClick={() => setOpen(true)}><Trash2 size={15} aria-hidden="true" /></button>
    {open && <ConfirmDelete projectId={projectId} node={node} hasChildren={hasChildren} onDeleted={onDeleted} onClose={() => setOpen(false)} />}</>;
}
